// @flow

import React, { useCallback, useState, useRef, useEffect } from "react";
import { Text, Input, Flex } from "@unifize/sarah";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { DragHandle } from "src/components/Unifize/DragHandle";
import DragHandleIcon from "src/components/Unifize/DragHandle";
import Icon from "src/icons";
import * as colors from "src/styles/constants/colors";

import {
  DefaultValueContainer,
  MakeDefaultContainer,
  OptionRow,
  ActionsContainer,
} from "./styles";

type Props = {
  id: string,
  option: string,
  onRemove: Function,
  isMultiple: boolean,
  isDefault: boolean,
  setDefaultValue: Function,
  onEdit: Function,
};

const Option = ({
  id,
  option,
  onRemove,
  isMultiple,
  isDefault,
  setDefaultValue,
  onEdit,
}: Props) => {
  const { listeners, setNodeRef, setActivatorNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editedOption, setEditedOption] = useState(option);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleRemove = (e: any) => {
    e.stopPropagation();
    onRemove(option);
  };

  const handleSave = (e: any) => {
    e.stopPropagation();
    if (editedOption.trim() !== option && editedOption.trim() !== "") {
      onEdit(option, editedOption.trim());
    }
    setIsEditing(false);
    // $FlowFixMe - optional chaining not yet supported
    inputRef.current?.blur();
  };

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      handleSave(e);
    }
  };

  const toggleDefaultValue = useCallback(() => {
    if (isMultiple) {
      setDefaultValue((prev) => {
        const currentState = prev ?? [];
        if (!currentState.includes(option)) {
          return [...currentState, option];
        }

        return currentState.filter((prevOption) => prevOption !== option);
      });
    } else {
      setDefaultValue((prev) => {
        return prev === option || prev[0] === option ? [] : [option];
      });
    }
  }, [option, isMultiple]);

  return (
    <div ref={setNodeRef} style={style}>
      <Flex align="center" direction="row" space="3">
        <DragHandle
          dragHandleRef={setActivatorNodeRef}
          dragHandleListeners={listeners}
          icon={<DragHandleIcon />}
        />
        <OptionRow>
          <Input
            ref={inputRef}
            type="text"
            value={editedOption}
            onChange={(e) => setEditedOption(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyPress}
            style={{
              width: "576px",
              maxWidth: "576px",
            }}
          />
          <ActionsContainer>
            <Icon
              type="delete"
              height={16}
              width={16}
              style={{
                cursor: "pointer",
              }}
              onClick={handleRemove}
            />
            {isDefault ? (
              <DefaultValueContainer onClick={toggleDefaultValue}>
                <Icon
                  height={20}
                  width={20}
                  type="circularCheck"
                  color={colors.active}
                />
              </DefaultValueContainer>
            ) : (
              <MakeDefaultContainer onClick={toggleDefaultValue}>
                <Icon height={20} width={20} type="circularCheck" />
                <Text color={colors.grey42} truncate>
                  Make default value
                </Text>
              </MakeDefaultContainer>
            )}
          </ActionsContainer>
        </OptionRow>
      </Flex>
    </div>
  );
};

export default Option;
